<template>
  <article class="learning-goals">
    <h2 class="header">
      <CdnImage
        class="content-type-icon"
        :image-path="'/fessor/bookshelf/lesson-intro-learning-objectives.svg'"
      ></CdnImage>
      {{ $t('labels.learning-goals') }}
      <TextToSpeech :text="$t('labels.learning-goals')" />
    </h2>
    <ol
      class="learning-goals"
      ref="learningGoals"
      :style="listStyle"
      @transitionend="onTransitionEnd"
      @transitionstart="onTransitionStart"
    >
      <li
        v-for="(item, index) in learningGoals"
        v-show="showAllItems || index < startItems"
        :key="item"
      >
        <span v-html="item" />
        <TextToSpeech :text="item" />
      </li>
    </ol>

    <div
      class="footer"
      @click="toggleOpenClose"
      v-if="startItems < learningGoals.length"
      role="button"
    >
      <span>{{ buttonText }}</span>
      <FaIcon
        icon="angle-down"
        class="footer-icon"
        :class="{ rotate: isExpanded }"
      />
    </div>
  </article>
</template>

<script>
import CdnImage from '../ui/CdnImage'
import FaIcon from '@/components/ui/FaIcon'
import TextToSpeech from '@/components/accessibility/TextToSpeech'

import typesetMathMlMixin from '@/mixins/typeset-mathml'

import { cssSizeToPx } from '@/utils/style'

export default {
  name: 'LearningGoals',
  mixins: [typesetMathMlMixin],
  components: {
    CdnImage,
    FaIcon,
    TextToSpeech,
  },
  props: {
    learningGoals: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      /**
       * The current expanded/opened state.
       * @type {boolean}
       */
      isExpanded: false,
      /**
       * Mirrors isExpanded based on the transition events emitted by the list element,
       * to make sure the elements aren't removed before the animation ends.
       * @type {boolean}
       */
      showAllItems: false,
      /**
       * The cached height of the first list item.
       * @type {number}
       */
      itemHeight: 0,
      /**
       * The number of items to display before expanding.
       * @type {number}
       */
      startItems: 3,
    }
  },
  computed: {
    /**
     * Returns the text for the button
     * @return {string}
     */
    buttonText() {
      return this.isExpanded
        ? this.$t('components.learning-goals.show-less-button')
        : this.$t('components.learning-goals.show-more-button')
    },

    /**
     * Returns the style for the list item
     * Until the element is mounted, the height is set to auto.
     * Once the element is mounted and the height of a list item has been measured,
     * the height is set based on this measurement.
     * @return {{height: string}}
     */
    listStyle() {
      return {
        height:
          this.itemHeight === 0
            ? 'auto'
            : this.itemHeight *
                (this.isExpanded
                  ? this.learningGoals.length
                  : Math.min(this.startItems, this.learningGoals.length)) +
              'px',
      }
    },
  },
  mounted() {
    setTimeout(this.updateItemHeight)
  },

  methods: {
    onTransitionStart() {
      if (this.isExpanded) {
        this.showAllItems = true
      }
    },
    onTransitionEnd() {
      if (!this.isExpanded) {
        this.showAllItems = false
      }
    },
    /**
     * Updates the cached item height
     */
    updateItemHeight() {
      this.itemHeight = this.getLearningGoalsItemHeight()
    },

    /**
     * Get the height with margins of just one li in the list.
     * @return {Number}
     */
    getLearningGoalsItemHeight() {
      if (!this.$refs.learningGoals) {
        return 0
      }

      const li = this.$refs.learningGoals.querySelector('li')

      return Math.floor(
        li.offsetHeight +
          cssSizeToPx(getComputedStyle(li).getPropertyValue('margin-bottom'))
      )
    },

    toggleOpenClose() {
      this.updateItemHeight()

      // Toggle the expanded state
      this.isExpanded = !this.isExpanded
    },
  },
}
</script>

<style lang="scss" scoped>
.header {
  color: $textColor;
  text-transform: uppercase;
  font-size: 1.35em;
  margin: 0 0 1em 0;

  .content-type-icon {
    $size: 1.5em;
    display: inline-block;
    height: $size;
    width: $size;
    vertical-align: middle;
    margin: 0 0.5em 0.1em 0;
  }
}

ol.learning-goals {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  overflow: hidden;
  list-style: none;
  transition: height $transitionMedium ease-out;

  li {
    padding: 1em 1.5em 1em 1.5em;
    border-radius: 1em;
    background: var(--gray-light);
    margin: 0 0 1em 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.footer {
  display: flex;
  justify-content: center;
  font-size: 1.2em;
  cursor: pointer;

  span {
    padding: 0.2em 0.5em 0 0;
  }

  .footer-icon {
    font-size: 1.5em;
    transform: rotate(0deg);
    transition-duration: $transitionMedium;

    &.rotate {
      transform: rotate(180deg);
    }
  }
}
</style>
